import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import {
  Check,
  X,
  Plus,
  MapPin,
  Loader2,
  Info,
} from 'lucide-react';
import type { DiscoveredCity } from '@/services/ResearchService';

export interface SelectedCity {
  city: string;
  country: string;
  selected: boolean;
  relevance: string;
  has_hotels: boolean;
  place_id?: string;
  location_json?: any;
  source: 'llm' | 'manual' | 'search';
}

interface RegionCitySelectorProps {
  regionName: string;
  country: string;
  discoveredCities: DiscoveredCity[];
  isDiscovering: boolean;
  discoveryError: string | null;
  selectedCities: SelectedCity[];
  onCitiesChange: (cities: SelectedCity[]) => void;
  onRetryDiscovery: () => void;
}

export function RegionCitySelector({
  regionName,
  country,
  discoveredCities,
  isDiscovering,
  discoveryError,
  selectedCities,
  onCitiesChange,
  onRetryDiscovery,
}: RegionCitySelectorProps) {
  const [showAddCity, setShowAddCity] = useState(false);
  const [manualCityName, setManualCityName] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const selectedCount = selectedCities.filter(c => c.selected).length;

  function toggleCity(index: number) {
    const updated = [...selectedCities];
    updated[index] = { ...updated[index], selected: !updated[index].selected };
    onCitiesChange(updated);
  }

  function selectAll() {
    onCitiesChange(selectedCities.map(c => ({ ...c, selected: true })));
  }

  function deselectAll() {
    onCitiesChange(selectedCities.map(c => ({ ...c, selected: false })));
  }

  function removeCity(index: number) {
    onCitiesChange(selectedCities.filter((_, i) => i !== index));
  }

  function addManualCity() {
    if (!manualCityName.trim()) return;

    const newCity: SelectedCity = {
      city: manualCityName.trim(),
      country: country,
      selected: true,
      relevance: 'Manually added',
      has_hotels: true,
      source: 'manual',
    };

    // Check for duplicates
    const exists = selectedCities.some(
      c => c.city.toLowerCase() === newCity.city.toLowerCase() && c.country.toLowerCase() === newCity.country.toLowerCase()
    );
    if (!exists) {
      onCitiesChange([...selectedCities, newCity]);
    }
    setManualCityName('');
  }

  function handleSearchSelect(location: LocationData) {
    const newCity: SelectedCity = {
      city: location.city || location.formatted_address.split(',')[0].trim(),
      country: location.country || country,
      selected: true,
      relevance: 'Added via search',
      has_hotels: true,
      place_id: location.place_id,
      location_json: {
        formatted_address: location.formatted_address,
        place_id: location.place_id,
        geometry: {
          location: {
            lat: location.lat,
            lng: location.lon,
          },
        },
      },
      source: 'search',
    };

    // Check for duplicates
    const exists = selectedCities.some(
      c => c.city.toLowerCase() === newCity.city.toLowerCase() && c.country.toLowerCase() === newCity.country.toLowerCase()
    );
    if (!exists) {
      onCitiesChange([...selectedCities, newCity]);
    }
    setSearchValue('');
    setShowAddCity(false);
  }

  if (isDiscovering) {
    return (
      <div className="p-6 text-center">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin mx-auto mb-3" />
        <p className="text-sm text-gray-600">Discovering cities in {regionName}...</p>
        <p className="text-xs text-gray-400 mt-1">Using AI to find relevant destinations</p>
      </div>
    );
  }

  if (discoveryError) {
    return (
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <p className="text-sm text-red-800">Failed to discover cities: {discoveryError}</p>
        <Button variant="secondary" size="sm" onClick={onRetryDiscovery} className="mt-2">
          Retry Discovery
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with select controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            Cities in {regionName}
          </span>
          <Badge variant="secondary">
            {selectedCount} of {selectedCities.length} selected
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={selectAll}
            className="text-xs text-primary-600 hover:text-primary-800"
          >
            Select all
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={deselectAll}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Deselect all
          </button>
        </div>
      </div>

      {/* City list */}
      <div className="space-y-1 max-h-[240px] overflow-y-auto">
        {selectedCities.map((city, index) => (
          <div
            key={`${city.city}-${city.country}-${index}`}
            className={cn(
              'flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer',
              city.selected
                ? 'bg-primary-50 border-primary-200'
                : 'bg-gray-50 border-gray-200 opacity-60'
            )}
            onClick={() => toggleCity(index)}
          >
            {/* Checkbox */}
            <div className={cn(
              'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0',
              city.selected
                ? 'bg-primary-600 border-primary-600'
                : 'bg-white border-gray-300'
            )}>
              {city.selected && <Check className="w-3 h-3 text-white" />}
            </div>

            {/* City info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{city.city}</span>
                {city.country !== country && (
                  <span className="text-xs text-gray-500">{city.country}</span>
                )}
                {city.source === 'manual' && (
                  <Badge variant="secondary" className="text-xs">Manual</Badge>
                )}
                {city.source === 'search' && (
                  <Badge variant="secondary" className="text-xs">Search</Badge>
                )}
              </div>
              {city.relevance && city.source === 'llm' && (
                <p className="text-xs text-gray-500 ml-5.5 mt-0.5">{city.relevance}</p>
              )}
            </div>

            {!city.has_hotels && (
              <Badge variant="warning" className="flex-shrink-0 text-xs">No hotels</Badge>
            )}

            {/* Remove button for manually added cities */}
            {city.source !== 'llm' && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeCity(index);
                }}
                className="text-gray-400 hover:text-red-500 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add more cities */}
      {!showAddCity ? (
        <button
          type="button"
          onClick={() => setShowAddCity(true)}
          className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add more cities
        </button>
      ) : (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Add City</span>
            <button
              type="button"
              onClick={() => { setShowAddCity(false); setManualCityName(''); setSearchValue(''); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Search for city */}
          <LocationAutocomplete
            value={searchValue}
            onChange={setSearchValue}
            onLocationSelect={handleSearchSelect}
            placeholder="Search for a city..."
            types="cities"
          />

          {/* Or type manually */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">or type manually:</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={manualCityName}
              onChange={(e) => setManualCityName(e.target.value)}
              placeholder="City name"
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addManualCity();
                }
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={addManualCity}
              disabled={!manualCityName.trim()}
            >
              Add
            </Button>
          </div>
        </div>
      )}

      {/* Info hint */}
      {selectedCities.length > 0 && selectedCount === 0 && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Please select at least one city to create the region.
          </p>
        </div>
      )}
    </div>
  );
}
